import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import api from "@/lib/api";

const LOGO =
  "https://customer-assets.emergentagent.com/job_ed2227e8-f42f-486d-80b3-32e833e48098/artifacts/o750zne6_72B3D45F-1688-469F-935E-BB6BB3789CC9.png";

const MAX_POLLS = 15;
const POLL_INTERVAL_MS = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const session_id = params.get("session_id");
  const [state, setState] = useState({ status: "polling", details: null, error: null });

  useEffect(() => {
    if (!session_id) {
      setState({ status: "error", error: "Missing session id.", details: null });
      return;
    }
    let cancelled = false;
    let attempt = 0;

    const poll = async () => {
      if (cancelled) return;
      attempt += 1;
      try {
        const { data } = await api.get(`/payments/status/${session_id}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", details: data, error: null });
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setState({ status: "failed", details: data, error: `Payment ${data.payment_status}` });
          return;
        }
        if (attempt >= MAX_POLLS) {
          setState({ status: "timeout", details: data, error: null });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (attempt >= MAX_POLLS) {
          setState({ status: "error", error: "We couldn't verify your payment.", details: null });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [session_id]);

  return (
    <main data-testid="payment-success-page" className="min-h-screen bg-[#050B14] text-white flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-lg text-center">
        <Link to="/" className="inline-flex items-center gap-3 mb-12" data-testid="success-home-link">
          <img src={LOGO} alt="Capital Shield" className="h-11 w-11 object-contain" />
          <div className="text-left">
            <div className="font-display text-lg font-bold tracking-wide">CAPITAL SHIELD</div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#9BD0E8]">Sealing · Snow Removal</div>
          </div>
        </Link>

        <div className="bg-[#0B1B3D] border border-white/10 p-10">
          {state.status === "polling" && (
            <>
              <Loader2 size={40} className="animate-spin text-[#C9A24B] mx-auto mb-6" />
              <div className="hairline mb-4">Confirming</div>
              <h1 className="font-display text-2xl font-bold mb-3">Verifying your payment…</h1>
              <p className="text-white/60 text-sm">Hang tight — this usually takes a couple of seconds.</p>
            </>
          )}

          {state.status === "paid" && (
            <>
              <CheckCircle2 size={56} className="text-[#C9A24B] mx-auto mb-6" />
              <div className="hairline mb-4">Payment Received</div>
              <h1 className="font-display text-3xl font-bold mb-3" data-testid="success-title">Welcome to the crew.</h1>
              <p className="text-white/70 text-sm max-w-sm mx-auto leading-relaxed mb-8">
                Your booking is confirmed. We'll email your receipt and reach out within one business day to
                schedule your first visit and confirm access details.
              </p>
              {state.details?.amount != null && (
                <div className="inline-flex flex-col items-center border border-white/10 px-8 py-5 mb-8">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 mb-1">Amount Paid</div>
                  <div className="font-display text-2xl font-bold text-[#C9A24B]" data-testid="success-amount">
                    ${state.details.amount.toFixed(2)} {state.details.currency?.toUpperCase()}
                  </div>
                </div>
              )}
              <div>
                <Link
                  to="/"
                  data-testid="success-back-home"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#C9A24B] hover:bg-[#E0B962] text-[#050B14] font-semibold text-sm tracking-wide transition-colors"
                >
                  Return home
                </Link>
              </div>
            </>
          )}

          {(state.status === "failed" || state.status === "error") && (
            <>
              <AlertCircle size={48} className="text-red-400 mx-auto mb-6" />
              <h1 className="font-display text-2xl font-bold mb-3">Something went wrong</h1>
              <p className="text-white/70 text-sm max-w-sm mx-auto mb-8">
                {state.error || "Please try again, or contact us if you were charged."}
              </p>
              <Link
                to="/#pricing"
                className="inline-flex items-center gap-2 px-6 py-3 border border-white/20 hover:border-[#C9A24B] hover:text-[#C9A24B] text-sm font-medium transition-colors"
              >
                Try again
              </Link>
            </>
          )}

          {state.status === "timeout" && (
            <>
              <Loader2 size={40} className="text-[#C9A24B] mx-auto mb-6" />
              <h1 className="font-display text-2xl font-bold mb-3">Still processing…</h1>
              <p className="text-white/70 text-sm max-w-sm mx-auto mb-8">
                Your payment is still being confirmed by your bank. We'll email your receipt shortly — no need to pay again.
              </p>
              <Link to="/" className="text-[#C9A24B] hover:text-[#E0B962] text-sm underline underline-offset-4">Return home</Link>
            </>
          )}
        </div>
      </div>
    </main>
  );
}
